/**
 * ProtectedRoute — wrapper de rota autenticada.
 *
 * Redireciona para /login se não há sessão.
 * Renderiza tela de "Acesso não autorizado" se email não está em engineers.
 * Mostra loading enquanto hidrata a sessão.
 */

import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { session, loading, profileLoading, unauthorized, sessionExpired, signOut } = useAuth();
  const navigate = useNavigate();
  const [erroSaida, setErroSaida] = useState<string | null>(null);

  /**
   * Volta ao login — navegando só se a sessão foi realmente encerrada (GUPM-255).
   *
   * Antes havia um `finally` que navegava de qualquer jeito. A tela de login
   * aparecia com a sessão ainda viva no `localStorage`, então o *back* do
   * navegador devolvia o usuário logado. Pior que falhar: mentia para quem
   * estava tentando sair.
   */
  async function handleVoltarAoLogin() {
    setErroSaida(null);
    const { error } = await signOut('tela_sem_acesso');
    if (error) {
      setErroSaida(
        'Não foi possível sair: sua sessão continua ativa. Verifique a conexão e tente novamente.',
      );
      return;
    }
    navigate('/login', { replace: true });
  }

  if (loading || profileLoading) {
    return (
      <div className="min-h-screen bg-jf-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-jf-yellow border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Carregando...</p>
        </div>
      </div>
    );
  }

  if (sessionExpired) {
    return <Navigate to="/login" state={{ expired: true }} replace />;
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (unauthorized) {
    return (
      <div className="min-h-screen bg-jf-bg flex items-center justify-center p-6">
        <div className="bg-jf-surface border border-jf-border rounded-xl p-8 max-w-md text-center">
          <div className="text-4xl mb-4">🔒</div>
          <h1 className="text-white font-semibold text-lg mb-2">
            Acesso não autorizado
          </h1>
          <p className="text-slate-400 text-sm mb-6">
            Seu usuário não está cadastrado no GeoGuard UPM.
            Entre em contato com o TI JF Brasil para solicitar acesso.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <a
              href="mailto:dev@jfbr.com.br"
              className="inline-block bg-jf-yellow text-jf-bg font-semibold text-sm px-5 py-2 rounded-lg hover:brightness-110"
            >
              Contatar TI JFB
            </a>
            <button
              onClick={handleVoltarAoLogin}
              className="inline-block bg-jf-surface2 text-slate-200 border border-jf-border font-semibold text-sm px-5 py-2 rounded-lg hover:bg-jf-border"
            >
              Voltar ao login
            </button>
          </div>
          {erroSaida && (
            <p
              role="alert"
              className="mt-4 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs leading-snug text-red-300"
            >
              {erroSaida}
            </p>
          )}
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
